import styles from 'css/src/exports.module.scss';

import type {
    ControlProps,
    CSSObjectWithLabel,
    GroupBase,
    MultiValueProps,
    OptionProps,
    SingleValueProps,
    StylesConfig,
} from 'react-select';

import type { SelectOption } from 'js/src/components/common/ui/Select/types';
// V1 styles: https://github.com/JedWatson/react-select/tree/v1.x/scss
// V3 styles: https://react-select.com/styles

const getOptionColor = (props: OptionProps<SelectOption, boolean, GroupBase<SelectOption>>) => {
    if (props.isDisabled) {
        return styles['allColors-color-grey-500'];
    }
    return styles['allColors-color-grey-900'];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const customReactSelectStyles: StylesConfig<any, boolean, any> = {
    indicatorSeparator: (base: CSSObjectWithLabel) => {
        return {
            ...base,
            display: 'none',
        } as CSSObjectWithLabel;
    }, // this removes vertical line on the left hand-side of the dropdown arrow
    dropdownIndicator: (base: CSSObjectWithLabel) => {
        return {
            ...base,
            cursor: 'pointer',
        } as CSSObjectWithLabel;
    },
    control: (
        base: CSSObjectWithLabel,
        props: ControlProps<SelectOption, boolean, GroupBase<SelectOption>>,
    ) => {
        let backgroundColor: string;
        let borderColor: string;
        let hoverBorderColor: string;

        if (props.isDisabled) {
            backgroundColor = styles['allColors-color-grey-200'];
            borderColor = styles['allColors-color-grey-200'];
            hoverBorderColor = styles['allColors-color-grey-200'];
        } else {
            backgroundColor = styles['allColors-color-white'];
            borderColor = props.isFocused
                ? styles['allColors-color-l5-primary-500']
                : styles['allColors-color-grey-500'];
            hoverBorderColor = props.isFocused
                ? styles['allColors-color-l5-primary-500']
                : styles['allColors-color-grey-700'];
        }

        return {
            ...base,
            minHeight: '40px',
            cursor: 'pointer',
            boxShadow: 'none',
            backgroundColor,
            borderColor,
            '&:hover': {
                borderColor: hoverBorderColor,
            },
        } as CSSObjectWithLabel;
    },
    // Ensures portaled menus render above Bootstrap modals (z-index 1055)
    menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: 1056 }) as CSSObjectWithLabel,
    menu: (base: CSSObjectWithLabel) => {
        return {
            ...base,
            marginTop: '5px',
        } as CSSObjectWithLabel;
    },
    menuList: (base: CSSObjectWithLabel) => {
        return {
            ...base,
        } as CSSObjectWithLabel;
    },
    option: (
        base: CSSObjectWithLabel,
        props: OptionProps<SelectOption, boolean, GroupBase<SelectOption>>,
    ) => {
        return {
            ...base,
            color: getOptionColor(props),
            backgroundColor: props.isSelected
                ? styles['allColors-color-grey-200']
                : props.isFocused
                  ? styles['allColors-color-grey-300']
                  : styles['allColors-color-white'],
            ':active': {
                backgroundColor: props.isSelected
                    ? styles['allColors-color-grey-200']
                    : styles['allColors-color-grey-300'],
            },
            opacity: props.isDisabled ? '0.5' : '1',
            cursor: props.isDisabled ? 'not-allowed' : 'pointer',
        } as CSSObjectWithLabel;
    },
    valueContainer: (base: CSSObjectWithLabel) => {
        return {
            ...base,
            maxHeight: '200px',
            overflowY: 'auto',
        } as CSSObjectWithLabel;
    },
    group: (base: CSSObjectWithLabel) => {
        return {
            ...base,
            ':not(:last-child)': {
                borderBottom: `solid 1px ${styles['allColors-color-grey-300']}`,
            },
        } as CSSObjectWithLabel;
    },
    multiValue: (
        base: CSSObjectWithLabel,
        props: MultiValueProps<SelectOption, boolean, GroupBase<SelectOption>>,
    ) => {
        return {
            ...base,
            backgroundColor: props.data.hasError
                ? styles['allColors-color-error-200']
                : styles['allColors-color-grey-300'],
            borderRadius: '4px',
            border: props.data.hasError ? `1px solid ${styles['allColors-color-error-600']}` : '',
            '&:hover': {
                backgroundColor: props.data.hasError
                    ? styles['allColors-color-error-300']
                    : styles['allColors-color-grey-400'],
            },
        } as CSSObjectWithLabel;
    },
    multiValueLabel: (base: CSSObjectWithLabel) => {
        return {
            ...base,
            padding: '4px',
            paddingLeft: '8px',
            fontSize: '14px',
            lineHeight: '20px',
            color: styles['allColors-color-black'],
        } as CSSObjectWithLabel;
    },
    multiValueRemove: (
        base: CSSObjectWithLabel,
        props: MultiValueProps<SelectOption, boolean, GroupBase<SelectOption>>,
    ) => {
        return {
            ...base,
            '> i': {
                color: props.data.hasError
                    ? styles['allColors-color-error-600']
                    : styles['allColors-color-grey-700'],
                fontSize: '20px',
            },
            '&:hover': {
                backgroundColor: props.data.hasError
                    ? styles['allColors-color-error-300']
                    : styles['allColors-color-grey-400'],
                '> i': {
                    color: styles['allColors-color-error-900'],
                },
            },
        } as CSSObjectWithLabel;
    },
    singleValue: (
        base: CSSObjectWithLabel,
        props: SingleValueProps<SelectOption, boolean, GroupBase<SelectOption>>,
    ) =>
        ({
            ...base,
            color: getOptionColor(
                props as OptionProps<SelectOption, boolean, GroupBase<SelectOption>>,
            ),
        }) as CSSObjectWithLabel,
};
